<script setup lang="ts">
import {
  ref, onMounted, nextTick, computed,
} from 'vue';
import UserService from '@/service/user.service';
import RequestService from '@/service/request.service';
import Chart from 'chart.js/auto';
import { RequestStatus } from '@/types/order';

const leads = ref<any[]>([]);
const requests = ref<any[]>([]);
const partnerName = ref('');
const loadingLeads = ref(true);
const loadingRequests = ref(true);
const chartRef = ref<HTMLCanvasElement | null>(null);
const chartInstance = ref<any>(null);
const referralCode = ref<string | null>(null);
const loadingReferralCode = ref(true);
const copied = ref(false);

const userId = localStorage.getItem('userId');

function formatDate(date: any) {
  if (!date) return '—';
  if (date.toDate) return date.toDate().toLocaleDateString();
  if (date.seconds) return new Date(date.seconds * 1000).toLocaleDateString();
  return new Date(date).toLocaleDateString();
}

function getLeadName(uid: string) {
  const lead = leads.value.find((l) => l.userId === uid);
  return lead ? lead.fullName : '—';
}

function statusClass(status: number) {
  switch (status) {
    case RequestStatus.Completed:
      return 'text-green-400 font-semibold';
    case RequestStatus.Processing:
      return 'text-yellow-400 font-semibold';
    case RequestStatus['In Progress']:
    case RequestStatus['Waiting For Payment']:
      return 'text-blue-400 font-semibold';
    case RequestStatus.Cancelled:
      return 'text-red-400 font-semibold';
    default:
      return 'text-gray-300';
  }
}

const conversionRate = computed(() => {
  if (!leads.value.length) return 0;
  const leadsWithRequests = leads.value.filter((lead) => requests.value.some((r) => r.userId === lead.userId && r.status === 'paid'));
  return Math.round((leadsWithRequests.length / leads.value.length) * 100);
});

function renderChart() {
  if (!chartRef.value) return;
  if (chartInstance.value) {
    chartInstance.value.destroy();
  }
  // Group leads by registration month
  const monthly: Record<string, number> = {};
  leads.value.forEach((lead) => {
    let date = lead.registrationDate;
    if (date?.toDate) date = date.toDate();
    else if (date?.seconds) date = new Date(date.seconds * 1000);
    else date = new Date(date);
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
      2,
      '0',
    )}`;
    monthly[key] = (monthly[key] || 0) + 1;
  });
  const labels = Object.keys(monthly).sort();
  const data = labels.map((l) => monthly[l]);
  chartInstance.value = new Chart(chartRef.value, {
    type: 'line',
    data: {
      labels,
      datasets: [
        {
          label: 'Leads',
          data,
          borderColor: '#C4992A',
          backgroundColor: 'rgba(196,153,42,0.2)',
          tension: 0.4,
          fill: true,
          pointBackgroundColor: '#fff',
          pointBorderColor: '#C4992A',
          pointRadius: 5,
        },
      ],
    },
    options: {
      plugins: {
        legend: {
          labels: { color: '#C4992A', font: { size: 16, weight: 'bold' } },
        },
      },
      scales: {
        x: { ticks: { color: '#fff' }, grid: { color: '#333' } },
        y: { ticks: { color: '#fff' }, grid: { color: '#333' } },
      },
      animation: { duration: 1200, easing: 'easeOutQuart' },
      responsive: true,
      maintainAspectRatio: false,
    },
  });
}

async function loadLeadsAndRequests() {
  loadingLeads.value = true;
  loadingRequests.value = true;
  if (!userId) return;
  // Fetch leads
  const leadsResult = await UserService.getUsersReferredBy(userId);
  leads.value = leadsResult;
  partnerName.value = leadsResult[0]?.referredByName || leadsResult[0]?.partnerName || '';
  loadingLeads.value = false;
  // Fetch requests for those leads
  const leadUserIds = leadsResult.map((l: any) => l.userId);
  if (leadUserIds.length) {
    requests.value = await RequestService.getRequestsByUserIds(leadUserIds);
  } else {
    requests.value = [];
  }
  loadingRequests.value = false;
  // await nextTick();
  // renderChart();
}

async function fetchReferralCode() {
  loadingReferralCode.value = true;
  referralCode.value = await UserService.getReferralCodeByUserId(userId!);
  loadingReferralCode.value = false;
}

function copyReferralCode() {
  if (referralCode.value) {
    const message = `Download TALAB and use my referral code: ${referralCode.value}\n\nGet exclusive access to premium services and earn rewards!\n\nhttps://talabapp.co.uk/`;
    navigator.clipboard.writeText(message);
    copied.value = true;
    setTimeout(() => {
      copied.value = false;
    }, 1500);
  }
}

onMounted(() => {
  fetchReferralCode();
  loadLeadsAndRequests();
});
</script>

<template>
  <div class="min-h-screen bg-black-primary text-white p-6 font-inter">
    <div class="max-w-7xl mx-auto">

      <!-- Header -->
      <div
        class="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4"
      >
        <div>
          <h1
            class="text-3xl md:text-4xl font-bold text-gold mb-2 animate-fade-in"
          >
            Welcome, {{ partnerName || "Partner" }}
          </h1>
          <p class="text-lg text-gray-300 animate-fade-in-slow">
            Your personal dashboard to track your leads and their requests.
          </p>
        </div>
        <img
          src="@/assets/gold-logo-transparent.png"
          alt="TALAB Logo"
          class="w-24 md:w-32 animate-fade-in"
        />
      </div>

      <!-- Referral Code Card -->
      <div class="flex justify-center mb-8 animate-fade-in">
        <div
          class="bg-gradient-to-br from-[#BC8809] to-gold-customYellow rounded-2xl shadow-lg px-8 py-6 flex flex-col items-center w-full max-w-md relative"
        >
          <span class="text-lg font-semibold text-black mb-2"
            >Your Referral Code</span
          >
          <div
            v-if="loadingReferralCode"
            class="h-10 w-40 bg-zinc-200 rounded animate-pulse mb-2"
          ></div>
          <div v-else class="flex items-center gap-2">
            <span
              class="text-2xl font-bold tracking-widest text-black px-4 py-2 rounded-lg border-2 border-white select-all"
              >{{ referralCode || "N/A" }}</span
            >
            <button
              @click="copyReferralCode"
              class="ml-2 px-3 py-2 rounded-lg bg-gold-primary text-black font-semibold hover:bg-gold-secondary transition border border-gold-secondary focus:outline-none"
            >
              <span v-if="!copied">Copy</span>
              <span v-else>Copied!</span>
            </button>
          </div>
          <span class="text-xs text-black/60 mt-2"
            >Share this code with your leads.</span
          >
        </div>
      </div>

      <!-- Stat Cards & Chart -->
      <div v-if="leads.length > 0">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
          <div
            class="bg-gradient-to-br from-[#BC8809] to-gold-customYellow rounded-2xl p-6 shadow-lg flex flex-col items-center animate-pop-in"
          >
            <span class="text-4xl font-bold">{{ leads.length }}</span>
            <span class="text-lg font-medium mt-2">Total Leads</span>
          </div>
          <div
            class="bg-gradient-to-br from-[#BC8809] to-gold-customYellow rounded-2xl p-6 shadow-lg flex flex-col items-center animate-pop-in delay-100"
          >
            <span class="text-4xl font-bold">{{ requests.length }}</span>
            <span class="text-lg font-medium mt-2">Total Requests</span>
          </div>
          <div
            class="bg-gradient-to-br from-[#BC8809] to-gold-customYellow rounded-2xl p-6 shadow-lg flex flex-col items-center animate-pop-in delay-200"
          >
            <span class="text-4xl font-bold">{{ conversionRate }}%</span>
            <span class="text-lg font-medium mt-2">Lead Conversion</span>
          </div>
        </div>
        <!-- <div class="bg-neutral-800 rounded-2xl p-6 mb-10 shadow-lg animate-fade-in" style="height: 300px;">
          <h2 class="text-2xl font-semibold mb-4 text-gold">Leads Over Time</h2>
          <canvas ref="chartRef"></canvas>
        </div> -->
      </div>
      <div
        v-else
        class="flex flex-col items-center justify-center py-16 animate-fade-in"
      >
        <h2 class="text-2xl font-semibold text-gold mb-2">No referrals yet!</h2>
        <p class="text-lg text-gray-300 mb-4">
          Once you refer users, you’ll see your stats and leads here.
        </p>
        <span class="text-sm text-gray-500"
          >Share your referral link to get started!</span
        >
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-8" v-if="leads.length > 0">
        <!-- Leads Table -->
        <div
          class="bg-neutral-800 rounded-2xl p-6 shadow-lg animate-fade-in min-h-80"
        >
          <h2 class="text-xl font-semibold mb-4 text-gold">Your Leads</h2>
          <div v-if="loadingLeads" class="text-gray-400">Loading leads...</div>
          <div v-else-if="leads.length === 0" class="text-gray-400">
            No leads found.
          </div>
          <table v-else class="w-full text-left">
            <thead>
              <tr class="text-gold border-b border-gold/30">
                <th class="py-2">Name</th>
                <th class="py-2">Email</th>
                <th class="py-2">Registered</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="lead in leads"
                :key="lead.id"
                class="border-b border-zinc-700 hover:bg-zinc-800 transition"
              >
                <td class="py-2">{{ lead.fullName }}</td>
                <td class="py-2">{{ lead.email }}</td>
                <td class="py-2">
                  {{ formatDate(lead.registrationDate) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Requests Table -->
        <div class="bg-neutral-800 rounded-2xl p-6 shadow-lg animate-fade-in">
          <h2 class="text-xl font-semibold mb-4 text-gold">Leads' Requests</h2>
          <div v-if="loadingRequests" class="text-gray-400">
            Loading requests...
          </div>
          <div v-else-if="requests.length === 0" class="text-gray-400">
            No requests found.
          </div>
          <table v-else class="w-full text-left">
            <thead>
              <tr class="text-gold border-b border-gold/30">
                <th class="py-2">Lead</th>
                <th class="py-2">Service</th>
                <th class="py-2">Date</th>
                <th class="py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="req in requests"
                :key="req.id"
                class="border-b border-zinc-700 hover:bg-zinc-800 transition"
              >
                <td class="py-2">{{ getLeadName(req.userId) }}</td>
                <td class="py-2">{{ req.title || req.service || "—" }}</td>
                <td class="py-2">{{ formatDate(req.createdAt) }}</td>
                <td class="py-2">
                  <span :class="statusClass(req.status)">{{
                    RequestStatus[req.status]
                  }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.bg-black-primary {
  background: #181818;
}
.text-gold {
  @apply text-gold-primary;
}
.font-inter {
  font-family: "Inter", sans-serif;
}
.animate-fade-in {
  animation: fadeIn 1s ease;
}
.animate-fade-in-slow {
  animation: fadeIn 1.5s ease;
}
.animate-pop-in {
  animation: popIn 0.7s cubic-bezier(0.68, -0.55, 0.27, 1.55);
}
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}
@keyframes popIn {
  0% {
    opacity: 0;
    transform: scale(0.8);
  }
  80% {
    opacity: 1;
    transform: scale(1.05);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}
</style>
